import { useEffect } from 'react'
import { Content, PersonalizedVariant } from '@phrasee/phrasee-typings'
import { useQueryClient } from '@tanstack/react-query'

import { useAppSelector } from 'common/hooks/redux'
import useContent from 'common/hooks/useContent'
import useWebsockets, { WsResponseBase } from 'common/hooks/useWebsockets'
import { isComponentElement } from 'features/unifiedFlow/api/interfaces'
import { contentKeys } from 'features/unifiedFlow/api/queryKeys'

import { getIsPersonalized } from '../helpers'

import useVariantsToApproveQuery from './useVariantsToApproveQuery'

interface WsResponse extends WsResponseBase {
  personalization_id: string
  variants: PersonalizedVariant[]
}

const useHandleNewLanguageUpdate = () => {
  const queryClient = useQueryClient()
  const { invalidateAll: invalidateAllVariantsToApproveCountQuery } =
    useVariantsToApproveQuery()
  const { content } = useContent()
  const accountId = useAppSelector((state) => state.authStates.accountId)

  const { lastJsonMessage }: { lastJsonMessage: WsResponse } = useWebsockets()

  const wsTopic = `accounts/${accountId}/new_language`

  const personalizedCampaigns = content?.elements.filter(getIsPersonalized)

  const hasPersonalizedVariantsLoading = personalizedCampaigns?.some(
    (element) =>
      element.personalized_variants?.some((variant) => variant.isLoading)
  )

  useEffect(() => {
    if (!content || !lastJsonMessage || hasPersonalizedVariantsLoading) {
      return
    }

    const wsTopics = lastJsonMessage?.topics
    const newLanguageTopic = wsTopics?.find((topic) => topic === wsTopic)

    if (!newLanguageTopic) {
      return
    }

    queryClient.setQueriesData<Content>(
      contentKeys.content(content._id),
      (old) => {
        if (!old) {
          return old
        }

        return {
          ...old,
          elements: old.elements.map((element) => {
            if (
              isComponentElement(element) &&
              element.personalization_id === lastJsonMessage.personalization_id
            ) {
              return {
                ...element,
                personalized_variants: [
                  ...(element.personalized_variants ?? []),
                  ...(lastJsonMessage.variants ?? []),
                ],
                personalized_ids_to_approve: [
                  ...(element.personalized_ids_to_approve ?? []),
                  ...lastJsonMessage.variants.map(
                    (variant) => variant.variant_id
                  ),
                ],
              }
            }
            return element
          }),
        }
      }
    )

    invalidateAllVariantsToApproveCountQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastJsonMessage])
}

export default useHandleNewLanguageUpdate
